// Brings the user to the field a validation message is about.
//
// Two entry points:
//  - startErrorAutoScroll(): installed once in App.js. Watches the DOM and,
//    right after the user clicks a button / presses Enter, scrolls to the first
//    inline error (.field-error etc.) so nobody has to hunt for it. It ignores
//    errors that appear while the user is just typing.
//  - scrollToField(target): for toast-only validations. Pass a field name
//    (matches [data-field="name"], #name or [name="name"]), a selector or an
//    element — see showToast's `field` option.

// Error messages (must contain text).
const ERROR_TEXT_SELECTOR = ".field-error, .vendor-field-error, .chpwd-error";

// Fields flagged only by a red border / class (no message element).
const ERROR_MARK_SELECTOR =
  ".input-error, .field-invalid, .is-invalid, .upload-field-invalid";

// Primary action buttons: pressing one again with the same errors on screen
// should still bring the user back to the first error.
const PRIMARY_SELECTOR = ".gradientBtn, [type='submit']";

// How long after a click / Enter an error is treated as the result of it
// (covers validation that waits for an API response).
const INTENT_WINDOW_MS = 8000;

const FOCUSABLE_SELECTOR =
  "input:not([type='hidden']):not([type='checkbox']):not([type='radio']):not([type='file']):not([readonly]):not([disabled]), textarea:not([readonly]):not([disabled])";

// Focusing these would pop open a picker / menu.
const NO_FOCUS_SELECTOR =
  "[class*='datepicker' i], [class*='timepicker' i], [class*='select' i]";

const isVisible = (el) => el.getClientRects().length > 0;

// querySelectorAll returns matches in document order, so [0] is the topmost.
const getVisibleErrors = () =>
  Array.from(
    document.querySelectorAll(`${ERROR_TEXT_SELECTOR}, ${ERROR_MARK_SELECTOR}`),
  ).filter(
    (el) =>
      isVisible(el) &&
      (el.matches(ERROR_MARK_SELECTOR) || el.textContent.trim()),
  );

const focusNear = (el) => {
  const container = el.closest("[class*='col-']") || el.parentElement;

  const input = Array.from(container?.querySelectorAll(FOCUSABLE_SELECTOR) || [])
    .filter(isVisible)
    .find((candidate) => !candidate.closest(NO_FOCUS_SELECTOR));

  input?.focus({ preventScroll: true });
};

const scrollTo = (el) => {
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  focusNear(el);
};

export const scrollToFirstError = () => {
  const [first] = getVisibleErrors();

  if (first) scrollTo(first);

  return Boolean(first);
};

const escape = (value) =>
  window.CSS?.escape ? window.CSS.escape(value) : String(value);

const resolveField = (target) => {
  if (!target) return null;

  if (typeof target !== "string") return target.current ?? target;

  return (
    document.querySelector(`[data-field="${escape(target)}"]`) ||
    document.getElementById(target) ||
    document.querySelector(`[name="${escape(target)}"]`) ||
    (() => {
      try {
        return document.querySelector(target);
      } catch {
        return null;
      }
    })()
  );
};

export const scrollToField = (target) => {
  const el = resolveField(target);

  if (!el) return false;

  el.scrollIntoView({ behavior: "smooth", block: "center" });

  const focusable = el.matches(FOCUSABLE_SELECTOR)
    ? el
    : el.querySelector(FOCUSABLE_SELECTOR);

  if (focusable && !focusable.closest(NO_FOCUS_SELECTOR)) {
    focusable.focus({ preventScroll: true });
  }

  return true;
};

export const startErrorAutoScroll = () => {
  let lastIntent = 0;
  let observerTimer = null;
  let seen = new WeakSet();

  const check = ({ gated, force }) => {
    const errors = getVisibleErrors();

    if (!errors.length) {
      seen = new WeakSet();
      return;
    }

    const hasNew = errors.some((el) => !seen.has(el));

    errors.forEach((el) => seen.add(el));

    if (gated && (hasNew || force)) scrollTo(errors[0]);
  };

  const hasIntent = () => Date.now() - lastIntent < INTENT_WINDOW_MS;

  const onClick = (event) => {
    const button = event.target?.closest?.(
      "button, [type='submit'], [role='button']",
    );

    if (!button || button.disabled) return;

    lastIntent = Date.now();

    const force = button.matches(PRIMARY_SELECTOR);

    // Let React commit the validation result first.
    setTimeout(() => check({ gated: true, force }), 150);
  };

  const onKeyDown = (event) => {
    const { target } = event;

    if (event.key === "Enter" && target?.tagName === "INPUT") {
      lastIntent = Date.now();
      setTimeout(() => check({ gated: true, force: true }), 150);
      return;
    }

    // Typing means errors that show up now are live validation, not a
    // failed submit — don't jump the page while someone is entering text.
    if (event.key !== "Enter") lastIntent = 0;
  };

  const observer = new MutationObserver(() => {
    clearTimeout(observerTimer);

    observerTimer = setTimeout(
      () => check({ gated: hasIntent(), force: false }),
      80,
    );
  });

  document.addEventListener("click", onClick, true);
  document.addEventListener("keydown", onKeyDown, true);
  observer.observe(document.body, { childList: true, subtree: true });

  return () => {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("keydown", onKeyDown, true);
    observer.disconnect();
    clearTimeout(observerTimer);
  };
};
